// The reset ritual: the chair, the headphones, a timer. Two taps from "End
// of day" to a running timer: this page, then a size. Brown noise from the
// device, a firm alarm at the end, and one line after: stand up. After
// seven in the evening the awake version is the one to run, so night sleep
// is left alone. Everything is logged to the day; nothing is scored.
import { useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Volume2, VolumeX, Square } from 'lucide-react'
import { useNow } from '@/hooks/useNow'
import { RESET_SIZES, type ResetSize } from '@/kinds/day/schema'
import { STAND_UP, eveningOf } from '@/lib/wellbeing'
import { lateForSleep, useReset } from '@/state/reset'
import { Button, PageTitle, cx } from '@/components/ui'

const mmss = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** Every mounted view with the timer on it may end the session; finish() is idempotent. */
export const useResetClock = () => {
  const status = useReset((s) => s.status)
  const deadline = useReset((s) => s.deadline)
  const finish = useReset((s) => s.finish)
  const now = useNow(status === 'running' ? 1000 : undefined)
  useEffect(() => {
    if (status === 'running' && deadline <= now) void finish()
  }, [status, deadline, now, finish])
  return status === 'running' ? Math.max(0, deadline - now) : 0
}

const SIZES: { size: ResetSize; label: string; blurb: string }[] = [
  { size: 'full', label: 'Full', blurb: '30 minutes' },
  { size: 'short', label: 'Short', blurb: '10 minutes' },
  { size: 'minimum', label: 'Minimum', blurb: '5 minutes' },
]

export const ResetView = () => {
  const navigate = useNavigate()
  const r = useReset()
  const left = useResetClock()
  const now = useNow(60_000)
  const late = lateForSleep(now)
  const total = r.size ? RESET_SIZES[r.size] * 60_000 : 1
  const progress = r.status === 'running' ? 1 - left / total : 1
  const R = 118
  const circ = 2 * Math.PI * R

  if (r.status === 'done') {
    return (
      <div className="flex flex-col items-center gap-6 pt-10 text-center" data-testid="reset-done">
        <div className="text-2xl font-medium leading-snug max-w-xs">{STAND_UP}</div>
        <Button
          big
          onClick={() => {
            r.dismiss()
            void navigate({ to: '/' })
          }}
          data-testid="reset-dismiss"
        >
          Up
        </Button>
      </div>
    )
  }

  if (r.status === 'running') {
    return (
      <div className="flex flex-col items-center gap-6 pt-6" data-testid="reset-running">
        <svg width={280} height={280} viewBox="0 0 280 280" className="-rotate-90" aria-hidden>
          <circle cx={140} cy={140} r={R} fill="none" stroke="var(--color-line)" strokeWidth={6} />
          <circle
            cx={140}
            cy={140}
            r={R}
            fill="none"
            stroke="var(--color-accent)"
            strokeWidth={6}
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={circ * (1 - progress)}
            style={{ transition: 'stroke-dashoffset 900ms linear' }}
          />
        </svg>
        <div className="-mt-[196px] text-6xl font-light tabular-nums" data-testid="reset-time">
          {mmss(left)}
        </div>
        <div className="mt-[100px] text-sm text-muted">
          {SIZES.find((s) => s.size === r.size)?.label} · {late ? 'awake, eyes open' : 'eyes closed is fine'}
        </div>
        <div className="flex gap-2">
          <Button
            variant={r.audio === 'noise' ? 'chipOn' : 'chip'}
            onClick={r.toggleAudio}
            data-testid="reset-audio"
          >
            {r.audio === 'noise' ? <Volume2 size={14} /> : <VolumeX size={14} />}
            {r.audio === 'noise' ? 'Brown noise' : 'Silence'}
          </Button>
          <Button variant="chip" onClick={() => void r.stop()} data-testid="reset-stop">
            <Square size={12} /> Stop
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4" data-testid="reset">
      <PageTitle>Reset</PageTitle>
      <p className="text-sm text-muted -mt-2">
        The chair, the headphones, nothing else. Pick a size and it starts.
      </p>
      {late && (
        <p
          className="text-sm rounded-[var(--radius-card)] border border-line bg-raised px-3 py-2.5"
          data-testid="reset-late"
        >
          After seven: run it awake. Eyes open, sound on, sitting up, so tonight's sleep stays whole.
        </p>
      )}
      <div className="grid grid-cols-3 gap-2">
        {SIZES.map((s) => (
          <button
            key={s.size}
            type="button"
            onClick={() => void r.start(eveningOf(Date.now()), s.size, r.audio)}
            className={cx(
              'rounded-[var(--radius-card)] border border-line bg-raised px-3 py-5 flex flex-col items-center gap-1',
              'active:scale-[0.98] transition-transform hover:border-line-strong',
            )}
            data-testid={`reset-${s.size}`}
          >
            <span className="text-lg font-medium">{s.label}</span>
            <span className="text-xs text-muted">{s.blurb}</span>
          </button>
        ))}
      </div>
      <div>
        <Button
          variant={r.audio === 'noise' ? 'chipOn' : 'chip'}
          onClick={r.toggleAudio}
          data-testid="reset-audio"
        >
          {r.audio === 'noise' ? <Volume2 size={14} /> : <VolumeX size={14} />}
          {r.audio === 'noise' ? 'Brown noise' : 'Silence'}
        </Button>
      </div>
    </div>
  )
}
